import { useMemo } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, FileText, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatCurrency } from '@/lib/utils';
import type { SkuCatalogItem } from '../../api';
import type { V3QuoteSku, V3QuoteSubtotal, Language, Currency, Pricelist } from '../../types';
import { SkuCatalogCombobox } from './SkuCatalogCombobox';
import {
  calculateLineTotal,
  calculateGrandTotal,
  isNoteRow,
  createEmptyProductRow,
  createNoteRow,
  reorderSkus,
  buildDisplayRows,
} from '../../utils/skuCalculations';

interface QuoteSkuEditorProps {
  skus: V3QuoteSku[];
  subtotals: V3QuoteSubtotal[];
  onSkusChange: (skus: V3QuoteSku[]) => void;
  onSubtotalsChange: (subtotals: V3QuoteSubtotal[]) => void;
  language: Language;
  currency: Currency;
  disabled?: boolean;
  invoicedQuantities?: number[];
  pricelist?: Pricelist;
}

export function QuoteSkuEditor({
  skus,
  subtotals,
  onSkusChange,
  onSubtotalsChange,
  language,
  currency,
  disabled,
  invoicedQuantities = [],
  pricelist,
}: QuoteSkuEditorProps) {
  const { t } = useTranslation('quotes');

  const getSkuName = (item: SkuCatalogItem): string => {
    if (typeof item.name === 'object' && item.name !== null) {
      return language === 'fr' ? (item.name.fr || item.name.en || '') : (item.name.en || item.name.fr || '');
    }
    return language === 'fr' ? (item.name_fr || item.name || '') : (item.name || '');
  };

  const displayRows = useMemo(() => buildDisplayRows(skus, subtotals), [skus, subtotals]);

  const handleAddProduct = (subtotalId?: string) => {
    const newRow = createEmptyProductRow(subtotalId);
    onSkusChange([...skus, newRow]);
  };

  const handleAddNote = (subtotalId?: string) => {
    const newRow = createNoteRow(subtotalId);
    onSkusChange([...skus, newRow]);
  };

  const handleAddSection = () => {
    const id = `subtotal-${Date.now()}`;
    onSubtotalsChange([...subtotals, { _id: id, displayName: '' }]);
  };

  const handleRenameSection = (subtotalIndex: number, newName: string) => {
    // Only the display label changes on rename. The section's _id is a stable,
    // unique grouping key (assigned at creation) and must NOT be derived from the
    // name — otherwise two sections sharing a name collide on the same _id and
    // mirror each other's rows (buildDisplayRows groups by _id).
    const updated = [...subtotals];
    updated[subtotalIndex] = { ...updated[subtotalIndex], displayName: newName };
    onSubtotalsChange(updated);
  };

  const handleRemoveSection = (subtotalIndex: number) => {
    const sectionId = subtotals[subtotalIndex]._id;
    onSubtotalsChange(subtotals.filter((_, i) => i !== subtotalIndex));
    onSkusChange(skus.filter((sku) => sku.subtotal_id !== sectionId));
  };

  const productLabel = t('sku.autoSectionProduct');
  const serviceLabel = t('sku.autoSectionService');
  const autoSectionLabels = new Set([productLabel.toLowerCase(), serviceLabel.toLowerCase()]);

  const isAutoSection = (subtotal?: V3QuoteSubtotal) =>
    !!subtotal && autoSectionLabels.has((subtotal.displayName || '').trim().toLowerCase());

  // Removes Products/Services sections that no longer have any rows. Custom
  // user-named sections are preserved even when empty.
  const pruneEmptyAutoSections = (nextSkus: V3QuoteSku[]): V3QuoteSubtotal[] =>
    subtotals.filter(
      (s) => !isAutoSection(s) || nextSkus.some((sku) => sku.subtotal_id === s._id),
    );

  const handleRemoveRow = (index: number) => {
    const nextSkus = skus.filter((_, i) => i !== index);
    const nextSubtotals = pruneEmptyAutoSections(nextSkus);
    onSkusChange(nextSkus);
    if (nextSubtotals.length !== subtotals.length) onSubtotalsChange(nextSubtotals);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    onSkusChange(reorderSkus(skus, index, index - 1));
  };

  const handleMoveDown = (index: number) => {
    if (index >= skus.length - 1) return;
    onSkusChange(reorderSkus(skus, index, index + 1));
  };

  const updateRow = (index: number, updates: Partial<V3QuoteSku>) => {
    const updated = [...skus];
    updated[index] = { ...updated[index], ...updates };
    onSkusChange(updated);
  };

  const stripHtml = (html: string): string => {
    if (!html) return '';
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
      .replace(/<\/?(p|div|span|strong|em|b|i|u|ul|ol|li|h[1-6])[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const handleSkuSelect = (index: number, catalogItem: SkuCatalogItem) => {
    const rawDescription = language === 'fr'
      ? (catalogItem.description_fr || catalogItem.description_en || '')
      : (catalogItem.description_en || '');
    const description = stripHtml(rawDescription);
    const name = getSkuName(catalogItem);
    // Use pricelist-specific price if available, otherwise fall back to catalog value
    const pricelistSku = pricelist?.skus?.find(ps => ps.sku === catalogItem._id);
    const unitPrice = pricelistSku?.unit_price ?? catalogItem.value ?? 0;

    // Auto-section: route the row into the type-derived default section
    // ("Products" or "Services"). Reuses an existing section with that name,
    // otherwise creates one. If the row is in a custom-named section, leave it
    // alone — the user set it.
    const currentRow = skus[index];
    const currentSubtotal = currentRow?.subtotal_id
      ? subtotals.find((s) => s._id === currentRow.subtotal_id)
      : undefined;
    const inCustomSection = currentSubtotal && !isAutoSection(currentSubtotal);

    let targetSubtotalId = currentRow?.subtotal_id;
    let nextSubtotals = subtotals;
    if (!inCustomSection) {
      const isService = catalogItem.type === 'service';
      const sectionName = isService ? serviceLabel : productLabel;
      const existing = subtotals.find(
        (s) => (s.displayName || '').trim().toLowerCase() === sectionName.toLowerCase(),
      );
      if (existing) {
        targetSubtotalId = existing._id;
      } else {
        targetSubtotalId = sectionName.toLowerCase().replace(/\s+/g, '-');
        nextSubtotals = [...subtotals, { _id: targetSubtotalId, displayName: sectionName }];
      }
    }

    const nextSkus = [...skus];
    nextSkus[index] = {
      ...nextSkus[index],
      sku: catalogItem.sku,
      sku_id: catalogItem._id,
      name,
      description,
      unit_price: unitPrice,
      subtotal_id: targetSubtotalId,
    };

    // Drop any auto-section that the move left empty.
    const prunedSubtotals = nextSubtotals.filter(
      (s) => !isAutoSection(s) || nextSkus.some((sku) => sku.subtotal_id === s._id),
    );

    onSkusChange(nextSkus);
    if (prunedSubtotals.length !== subtotals.length
      || prunedSubtotals.some((s, i) => s._id !== subtotals[i]?._id)) {
      onSubtotalsChange(prunedSubtotals);
    }
  };

  const grandTotal = calculateGrandTotal(skus);

  const renderSkuRow = (sku: V3QuoteSku, skuIndex: number, rowKey: string) => {
    const isNote = isNoteRow(sku);
    const lineTotal = isNote ? 0 : calculateLineTotal(sku.quantity, sku.unit_price, sku.discount);

    return (
      <tr key={rowKey} className="border-t align-top">
        <td className="px-2 py-1.5 text-muted-foreground">{skuIndex + 1}</td>

        {isNote ? (
          <>
            <td className="px-2 py-1.5">
              <span className="text-xs text-muted-foreground italic">{t('sku.addNote')}</span>
            </td>
            <td colSpan={5} className="px-2 py-1.5">
              <Input
                value={sku.description || ''}
                onChange={(e) => updateRow(skuIndex, { description: e.target.value })}
                placeholder={t('sku.notePlaceholder')}
                className="h-8 text-sm"
                disabled={disabled}
              />
            </td>
            <td className="px-2 py-1.5 text-right text-muted-foreground">&mdash;</td>
          </>
        ) : (
          <>
            <td className="px-2 py-1.5">
              <SkuCatalogCombobox
                value={sku.sku}
                language={language}
                onSelect={(catalogItem) => handleSkuSelect(skuIndex, catalogItem)}
                disabled={disabled}
              />
            </td>
            <td className="px-2 py-1.5">
              <Textarea
                value={sku.description || ''}
                onChange={(e) => updateRow(skuIndex, { description: e.target.value })}
                rows={3}
                className="min-h-[4.5rem] text-sm py-1.5 px-2 leading-snug resize-y [field-sizing:content]"
                disabled={disabled}
              />
            </td>
            <td className="px-2 py-1.5 align-top">
              <Input
                type="number"
                min={0}
                value={sku.quantity}
                onChange={(e) => updateRow(skuIndex, { quantity: Number(e.target.value) })}
                className="h-8 text-sm text-right w-16"
                disabled={disabled}
              />
            </td>
            <td className="px-2 py-1.5 text-right text-muted-foreground text-xs">
              {invoicedQuantities[skuIndex] || '\u2014'}
            </td>
            <td className="px-2 py-1.5">
              <Input
                type="number"
                min={0}
                step={0.01}
                value={sku.unit_price}
                onChange={(e) => updateRow(skuIndex, { unit_price: Number(e.target.value) })}
                className="h-8 text-sm text-right w-24"
                disabled={disabled}
              />
            </td>
            <td className="px-2 py-1.5">
              <Input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={sku.discount || 0}
                onChange={(e) => updateRow(skuIndex, { discount: Number(e.target.value) })}
                className="h-8 text-sm text-right w-16"
                disabled={disabled}
              />
            </td>
            <td className="px-2 py-1.5 text-right font-medium">
              {formatCurrency(lineTotal, currency)}
            </td>
          </>
        )}

        <td className="px-2 py-1.5">
          <div className="flex gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleMoveUp(skuIndex)}
              disabled={disabled || skuIndex === 0}
              title={t('sku.moveUp')}
            >
              <ArrowUp className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleMoveDown(skuIndex)}
              disabled={disabled || skuIndex >= skus.length - 1}
              title={t('sku.moveDown')}
            >
              <ArrowDown className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={() => handleRemoveRow(skuIndex)}
              disabled={disabled}
              title={t('sku.remove')}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-3">
      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-2 py-2 font-medium w-8">#</th>
              <th className="text-left px-2 py-2 font-medium w-36">{t('sku.sku')}</th>
              <th className="text-left px-2 py-2 font-medium">{t('sku.description')}</th>
              <th className="text-right px-2 py-2 font-medium w-16">{t('sku.qty')}</th>
              <th className="text-right px-2 py-2 font-medium w-16">{t('sku.invoicedQty')}</th>
              <th className="text-right px-2 py-2 font-medium w-24">{t('sku.unitPrice')}</th>
              <th className="text-right px-2 py-2 font-medium w-16">{t('sku.discount')}</th>
              <th className="text-right px-2 py-2 font-medium w-24">{t('sku.lineTotal')}</th>
              <th className="px-2 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">
                  {t('table.noResults')}
                </td>
              </tr>
            ) : (
              displayRows.map((row, i) => {
                if (row.type === 'section-header') {
                  return (
                    <tr key={`section-header-${row.subtotalIndex}`} className="border-t-2 bg-muted/30">
                      <td className="px-2 py-1.5">
                        <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                      </td>
                      <td colSpan={7} className="px-2 py-1.5">
                        <Input
                          value={row.subtotal.displayName}
                          onChange={(e) => handleRenameSection(row.subtotalIndex, e.target.value)}
                          placeholder={t('sku.addSection')}
                          className="h-8 text-sm font-semibold"
                          disabled={disabled}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-0.5 justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleAddProduct(row.subtotal._id)}
                            disabled={disabled}
                            title={t('sku.addProduct')}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveSection(row.subtotalIndex)}
                            disabled={disabled}
                            title={t('sku.remove')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                if (row.type === 'section-sum') {
                  return (
                    <tr key={`section-sum-${row.subtotalIndex}`} className="border-t bg-muted/20">
                      <td colSpan={7} className="px-2 py-1.5 text-right text-sm font-medium text-muted-foreground">
                        {t('sku.subtotal')}
                      </td>
                      <td className="px-2 py-1.5 text-right font-semibold">
                        {formatCurrency(row.amount, currency)}
                      </td>
                      <td></td>
                    </tr>
                  );
                }

                // type === 'sku'
                return renderSkuRow(row.sku, row.skuIndex, `sku-${row.skuIndex}-${i}`);
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Actions & Grand Total */}
      <div className="flex items-center justify-between">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={disabled}>
              <Plus className="h-4 w-4 mr-1" />
              {t('sku.addRow')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => handleAddProduct()}>
              <Plus className="h-4 w-4 mr-2" />
              {t('sku.addProduct')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAddNote()}>
              <FileText className="h-4 w-4 mr-2" />
              {t('sku.addNote')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAddSection()}>
              <Layers className="h-4 w-4 mr-2" />
              {t('sku.addSection')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="text-right">
          <span className="text-sm text-muted-foreground mr-3">{t('sku.grandTotal')}:</span>
          <span className="text-lg font-semibold">
            {formatCurrency(grandTotal, currency)}
          </span>
        </div>
      </div>
    </div>
  );
}
